import { valueAt, SCENARIO_T } from "../lib/calc";
import { formatValue } from "../lib/format";
import type { Factor } from "../data/schema";
import { cn } from "../lib/cn";

/**
 * A continuous slider for one factor. Position 0 = pessimistic, 0.5 = consensus,
 * 1 = optimistic, with tick marks at those three anchors. The live value reads
 * out on the right using the factor's declared format.
 *
 * Styling for the track/thumb lives in index.css under `.factor-range`.
 */
export default function FactorSlider({
  factor,
  position,
  onChange,
}: {
  factor: Factor;
  position: number;
  onChange: (t: number) => void;
}) {
  const value = valueAt(factor, position);
  const pct = position * 100;

  return (
    <div className="select-none">
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <span className="text-[11px] uppercase tracking-wider text-white/60">
          Pessimistic
        </span>
        <span className="font-mono text-base text-cyan">
          {formatValue(value, factor.format)}
          {factor.unit ? (
            <span className="ml-1 text-xs text-white/55">{factor.unit}</span>
          ) : null}
        </span>
        <span className="text-[11px] uppercase tracking-wider text-white/60">
          Optimistic
        </span>
      </div>

      <div className="relative">
        {/* Filled portion of the track, drawn under the native input. */}
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-gradient-to-r from-cyan-deep to-cyan"
          style={{ width: `${pct}%` }}
        />
        <input
          type="range"
          min={0}
          max={1}
          step={0.005}
          value={position}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          // Double-click / double-tap snaps back to consensus.
          onDoubleClick={() => onChange(SCENARIO_T.consensus)}
          aria-label={`${factor.title} estimate`}
          className="factor-range relative z-10 w-full"
        />

        {/* Anchor tick marks (pessimistic / consensus / optimistic). */}
        <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2">
          {[0, 0.5, 1].map((t) => (
            <span
              key={t}
              className={cn(
                "absolute h-2.5 w-0.5 -translate-x-1/2 rounded-full",
                t === 0.5 ? "bg-white/40" : "bg-white/20",
              )}
              style={{ left: `${t * 100}%` }}
            />
          ))}
        </div>
      </div>

      <div className="mt-1.5 text-center text-[11px] uppercase tracking-wider text-white/55">
        Consensus
      </div>
    </div>
  );
}
